import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { useEmployee } from '../contexts/EmployeeContext';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  QueueListIcon,
  TruckIcon,
  ArchiveBoxIcon,
  CubeIcon,
  UsersIcon,
  BoltIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClockIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MapPinIcon,
  BuildingStorefrontIcon,
  WalletIcon,
  GlobeAltIcon,
  PrinterIcon,
  WrenchScrewdriverIcon,
  ShieldExclamationIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';

interface NavItem {
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  path: string;
  permission: string; // Permission key required to see this item
}

interface SettingsSubItem {
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  hash: string;
}

interface OrdersSubItem {
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  path: string;
}

interface SidebarProps {
  onClose?: () => void;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', icon: HomeIcon, path: '/', permission: 'dashboard' },
  // Orders is now a dropdown - see ordersSubNav below
  { name: 'Packing Station', icon: QueueListIcon, path: '/packing', permission: 'packing' },
  { name: 'Shipping Station', icon: TruckIcon, path: '/shipping', permission: 'shipping' },
  { name: 'Rates Estimator', icon: CalculatorIcon, path: '/rates-estimator', permission: 'shipping' },
  { name: 'Products', icon: CubeIcon, path: '/products', permission: 'products' },
  { name: 'Employees', icon: UsersIcon, path: '/employees', permission: 'employees' },
  { name: 'Automation', icon: BoltIcon, path: '/automation', permission: 'automation' },
  { name: 'Customers', icon: UserGroupIcon, path: '/customers', permission: 'customers' },
  { name: 'Blocked Addresses', icon: ShieldExclamationIcon, path: '/blocked-addresses', permission: 'blocked-addresses' },
  { name: 'Reports', icon: ChartBarIcon, path: '/reports', permission: 'reports' },
  { name: 'Time Report', icon: ClockIcon, path: '/time-report', permission: 'time-report' },
];

// Orders sub-navigation items
const ordersSubNav: OrdersSubItem[] = [
  { name: 'Orders', icon: ClipboardDocumentListIcon, path: '/orders' },
  { name: 'Shipments', icon: ArchiveBoxIcon, path: '/shipments' },
];

// Settings sub-navigation items
const settingsSubNav: SettingsSubItem[] = [
  { name: 'Locations', icon: MapPinIcon, hash: 'locations' },
  { name: 'Carriers', icon: TruckIcon, hash: 'carriers' },
  { name: 'Store Connections', icon: BuildingStorefrontIcon, hash: 'store' },
  { name: 'Wallet', icon: WalletIcon, hash: 'wallet' },
  { name: 'Packages', icon: ArchiveBoxIcon, hash: 'packages' },
  { name: 'International', icon: GlobeAltIcon, hash: 'international' },
  { name: 'Printers', icon: PrinterIcon, hash: 'printers' },
  { name: 'Team', icon: UserGroupIcon, hash: 'team' },
  { name: 'Advanced', icon: WrenchScrewdriverIcon, hash: 'advanced' },
];

export const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const location = useLocation();
  const { hasPermission } = useEmployee();
  
  // Track if settings is expanded - auto-expand if on settings page
  const isOnSettingsPage = location.pathname === '/settings';
  const [settingsExpanded, setSettingsExpanded] = useState(isOnSettingsPage);
  
  // Track if orders is expanded - auto-expand if on orders/shipments page
  const isOnOrdersPage = location.pathname.startsWith('/orders') || location.pathname === '/shipments';
  const [ordersExpanded, setOrdersExpanded] = useState(isOnOrdersPage);
  
  // Auto-expand settings when navigating to settings page
  useEffect(() => {
    if (isOnSettingsPage) {
      setSettingsExpanded(true);
    }
  }, [isOnSettingsPage]);
  
  // Auto-expand orders when navigating to orders/shipments page
  useEffect(() => {
    if (isOnOrdersPage) {
      setOrdersExpanded(true);
    }
  }, [isOnOrdersPage]);

  // Filter navigation to only show items the employee has permission for
  const allowedNavigation = navigation.filter(item => hasPermission(item.permission));
  const canAccessSettings = hasPermission('settings');
  const canAccessOrders = hasPermission('orders');
  
  // Get current settings hash
  const currentHash = location.hash.replace('#', '') || 'locations';

  return (
    <div className="w-64 max-w-[80vw] bg-white border-r border-slate-200 h-screen flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200 safe-px">
        <Link to="/" className="text-xl font-bold text-brand-blue tracking-tight">
          CraftyShip
        </Link>
        {/* Close button - only on mobile */}
        {onClose && (
          <button 
            onClick={onClose}
            className="lg:hidden p-1 rounded-lg hover:bg-slate-100 text-slate-500 tap-target"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
        {/* Dashboard */}
        {allowedNavigation.filter(item => item.path === '/').map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`
                flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all relative
                ${
                  isActive
                    ? 'bg-blue-50 text-brand-blue border-l-3 border-brand-blue ml-0 pl-3'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }
              `}
            >
              <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-brand-blue' : 'text-slate-400'}`} />
              {item.name}
            </Link>
          );
        })}
        
        {/* Orders with expandable sub-menu */}
        {canAccessOrders && (
          <div>
            <button
              onClick={() => setOrdersExpanded(!ordersExpanded)}
              className={`
                w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all relative
                ${
                  isOnOrdersPage
                    ? 'bg-blue-50 text-brand-blue'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }
              `}
            >
              <div className="flex items-center">
                <ClipboardDocumentListIcon className={`w-5 h-5 mr-3 flex-shrink-0 ${isOnOrdersPage ? 'text-brand-blue' : 'text-slate-400'}`} />
                Orders
              </div>
              {ordersExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {/* Sub-navigation items */}
            {ordersExpanded && (
              <div className="mt-1 ml-4 pl-4 border-l border-slate-200 space-y-0.5">
                {ordersSubNav.map((subItem) => {
                  const isSubActive = location.pathname === subItem.path;
                  const SubIcon = subItem.icon;
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      onClick={onClose}
                      className={`
                        flex items-center px-3 py-2 rounded-lg text-sm transition-all
                        ${
                          isSubActive
                            ? 'bg-blue-50 text-brand-blue font-medium'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        }
                      `}
                    >
                      <SubIcon className={`w-4 h-4 mr-2.5 flex-shrink-0 ${isSubActive ? 'text-brand-blue' : 'text-slate-400'}`} />
                      {subItem.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Rest of navigation items (excluding Dashboard) */}
        {allowedNavigation.filter(item => item.path !== '/').map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`
                flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all relative
                ${
                  isActive
                    ? 'bg-blue-50 text-brand-blue border-l-3 border-brand-blue ml-0 pl-3'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }
              `}
            >
              <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isActive ? 'text-brand-blue' : 'text-slate-400'}`} />
              {item.name}
            </Link>
          );
        })}
        
        {/* Settings with expandable sub-menu */}
        {canAccessSettings && (
          <div>
            <button
              onClick={() => setSettingsExpanded(!settingsExpanded)}
              className={`
                w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all relative
                ${
                  isOnSettingsPage
                    ? 'bg-blue-50 text-brand-blue'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }
              `}
            >
              <div className="flex items-center">
                <Cog6ToothIcon className={`w-5 h-5 mr-3 flex-shrink-0 ${isOnSettingsPage ? 'text-brand-blue' : 'text-slate-400'}`} />
                Settings
              </div>
              {settingsExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {/* Sub-navigation items */}
            {settingsExpanded && (
              <div className="mt-1 ml-4 pl-4 border-l border-slate-200 space-y-0.5">
                {settingsSubNav.map((subItem) => {
                  const isSubActive = isOnSettingsPage && currentHash === subItem.hash;
                  const SubIcon = subItem.icon;
                  return (
                    <Link
                      key={subItem.hash}
                      to={`/settings#${subItem.hash}`}
                      onClick={onClose}
                      className={`
                        flex items-center px-3 py-2 rounded-lg text-sm transition-all
                        ${
                          isSubActive
                            ? 'bg-blue-50 text-brand-blue font-medium'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        }
                      `}
                    >
                      <SubIcon className={`w-4 h-4 mr-2.5 flex-shrink-0 ${isSubActive ? 'text-brand-blue' : 'text-slate-400'}`} />
                      {subItem.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* User Profile Section */}
      <div className="p-4 border-t border-slate-200 safe-pb">
        <div className="flex items-center space-x-3 px-3 py-2">
          <UserButton
            afterSignOutUrl="/sign-in"
            appearance={{
              elements: {
                avatarBox: "w-8 h-8"
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
